import AOS from "aos";
import "aos/dist/aos.css";
import { auth, provider, signInWithPopup } from "./firebase-config";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthApiService from "../../ApiServices/AuthApiService";
import SigninWithGoogleRequest from "../../Models/SignInWithGoogleRequest";
import { useAuth } from "./AuthContext";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import { Typography } from "@material-tailwind/react";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import EnhancedLogo from "../Common/EnhancedLogo";

export default function Login() {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { setAuthInfo } = useAuth();

  const handleSignIn = async (e) => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    document.cookie = `clientTimeZone=${encodeURIComponent(timeZone)}; path=/;`;

    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    try {
      const credentials = { username: email, password, rememberMe: true };
      const result = await AuthApiService.signin(credentials);
      if (result.isSuccess) {
        const user = result.data.user;
        const role = result.data.roles;
        const token = result.data.token;
        localStorage.setItem("token", token);
        setAuthInfo(user, role);
        navigate("/home");
      } else {
        toast.error("Login failed. Please check your email or password");
        setErrorMessage("Login failed. Please check your credentials.");
      }
    } catch (error) {
      setErrorMessage("Failed to sign in. Please try again.");
      console.error("Error during sign-in:", error);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  const images = [
    "/Images/Login/dashboard2.jpeg",
  ];

  // Enhanced particle colors and effects
  const particleColors = [
    "rgba(255, 255, 255, 0.8)",  // White
    "rgba(200, 200, 255, 0.6)",  // Light blue
    "rgba(255, 200, 255, 0.6)",  // Light pink
    "rgba(200, 255, 255, 0.6)",  // Light cyan
  ];

  return (
    <div className="min-h-screen p-6 flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-indigo-50 to-purple-50">
      {/* Enhanced Floating Particles Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(100)].map((_, i) => {
          const size = Math.random() * 8 + 2;
          const duration = Math.random() * 15 + 10;
          const delay = Math.random() * 5;
          const color = particleColors[Math.floor(Math.random() * particleColors.length)];

          return (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                backgroundColor: color,
                width: `${size}px`,
                height: `${size}px`,
                x: Math.random() * window.innerWidth,
                y: Math.random() * window.innerHeight,
                filter: "blur(1px)",
              }}
              animate={{
                x: [null, Math.random() * 100 - 50],
                y: [null, Math.random() * 100 - 50],
                opacity: [0.8, 1, 0.8],
              }}
              transition={{
                duration: duration,
                delay: delay,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut",
              }}
            />
          );
        })}

        {/* Large floating blobs for depth */}
        <motion.div
          className="absolute rounded-full bg-purple-100/30 blur-3xl"
          style={{
            width: "400px",
            height: "400px",
            top: "20%",
            left: "10%",
          }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.4, 0.3]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut",
          }}
        />

        <motion.div
          className="absolute rounded-full bg-indigo-100/30 blur-3xl"
          style={{
            width: "500px",
            height: "500px",
            bottom: "10%",
            right: "10%",
          }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.4, 0.3]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut",
          }}
        />
      </div>

      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="flex flex-wrap w-full max-w-7xl justify-center gap-10 z-10"
      >

        {/* Right Side - Form Section */}
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="w-full md:w-[35%] bg-white/95 shadow-2xl backdrop-blur-lg rounded-xl p-8 border border-white/20"
        >
          <EnhancedLogo />

          <div className="max-w-md w-full mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 text-center">
              Welcome Back
            </h1>
            <p className="text-sm text-gray-500 font-medium mt-2 text-center">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="text-purple-600 hover:underline font-semibold"
              >
                Sign Up
              </Link>
            </p>

            <form className="space-y-6 mt-8" onSubmit={handleSignIn}>
              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-purple-600">*</span>
                </label>
                <input
                  type="email"
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none shadow-sm transition"
                  placeholder="abc@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password <span className="text-purple-600">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none shadow-sm transition"
                    placeholder="••••••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-purple-600 transition"
                  >
                    <FontAwesomeIcon
                      icon={showPassword ? faEyeSlash : faEye}
                      className="w-5 h-5"
                    />
                  </button>
                </div>
              </div>

              {/* Forgot Password */}
              {/* <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-purple-600 hover:underline font-semibold transition"
                >
                  Forgot Password?
                </Link>
              </div> */}

              {/* Submit Button */}
              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-3 rounded-lg font-medium transition-all shadow-lg"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : "Sign In"}
              </motion.button>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}